import { useState, useMemo, useId, useEffect } from "react";
import { useResizeObserver } from "../../hooks";
import { useScenario } from "../../state";
import { KPICard, Section, ChartDownloadButton } from "../../components";
import { Tornado } from "../../charts";
import { Model } from "../../model";
import { KPI_OPTIONS } from "./components";

const KPI_ACRONYMS = ["IRR", "NPV", "EBITDA", "TPC"];

/** Sentence-case the KPI label for prose, keeping finance acronyms upper-cased and
 *  the first character capitalised. "Geared IRR (dev exit)" → "Geared IRR (dev exit)". */
function titleCaseKpiLabel(label: string): string {
  let out = label.toLowerCase();
  for (const acro of KPI_ACRONYMS) {
    out = out.replace(new RegExp(`\\b${acro.toLowerCase()}\\b`, "g"), acro);
  }
  return out.charAt(0).toUpperCase() + out.slice(1);
}

export function Sensitivities() {
  const { active, result } = useScenario();
  const exitYear =
    active.assumptions.developerExitYear ?? active.assumptions.modelYears;
  const primaryGearedMetric =
    exitYear < active.assumptions.modelYears
      ? "developerGearedEarlyExitIrr"
      : "developerGearedIrr";
  const [kpi, setKpi] = useState(primaryGearedMetric);
  useEffect(() => setKpi(primaryGearedMetric), [primaryGearedMetric]);
  const kpiSelectId = useId().replace(/:/g, "-");
  const [tornadoRef, tornadoWidth] = useResizeObserver<HTMLDivElement>();
  const opt = KPI_OPTIONS.find((o) => o.key === kpi) ?? KPI_OPTIONS[0];

  const rows = useMemo(
    () => Model.tornado(active, kpi as keyof typeof result.kpis),
    [active, kpi],
  );
  const base = (() => {
    switch (kpi) {
      case "developerIrr":
        return result.kpis.developerIrr;
      case "developerUngearedEarlyExitIrr":
        return result.kpis.developerUngearedEarlyExitIrr;
      case "developerMarginOnCost":
        return result.kpis.developerMarginOnCost;
      case "developerYieldOnCost":
        return result.kpis.developerYieldOnCost;
      case "developerGearedIrr":
        return result.kpis.developerGearedIrr;
      case "developerGearedEarlyExitIrr":
        return result.kpis.developerGearedEarlyExitIrr;
      case "developerGearedWholeOfLifeIrr":
        return result.kpis.developerGearedWholeOfLifeIrr;
      case "developerYieldOnCash":
        return result.kpis.developerYieldOnCash;
      case "reddamNpv":
        return result.kpis.reddamNpv;
      case "avgEbitda":
        return result.kpis.avgEbitda;
      default:
        return 0;
    }
  })();

  return (
    <div className="page fade-in">
      <div className="page-strip">
        <div>
          <div className="eyebrow" style={{ marginBottom: 4 }}>
            Driver impact
          </div>
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-display)",
              fontSize: 28,
              fontWeight: 600,
            }}
          >
            Sensitivities
          </h2>
          <div className="lede" style={{ marginTop: 6 }}>
            Each driver is flexed up and down by its plausible range. Bars are
            the resulting KPI move from the {active.name} baseline.
          </div>
        </div>
        <div className="row gap-2">
          <label htmlFor={kpiSelectId} className="p-small">
            Target KPI
          </label>
          <select
            id={kpiSelectId}
            className="select"
            value={kpi}
            onChange={(e) => setKpi(e.target.value)}
          >
            {KPI_OPTIONS.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid g-3">
        <KPICard
          eyebrow={`Baseline ${opt.label}`}
          value={opt.fmt(base)}
          foot={active.name}
        />
        <KPICard
          eyebrow="Most volatile driver"
          value={rows[0]?.label || "—"}
          foot={
            rows[0]
              ? `Range ${opt.fmt(Model.tornadoBars(rows[0]).spread)}`
              : "No drivers"
          }
        />
        <KPICard
          eyebrow="Drivers under test"
          value={String(rows.length)}
          foot="Independent flex per row"
        />
      </div>

      <Section
        eyebrow="Tornado"
        title={`What drives ${titleCaseKpiLabel(opt.label)}?`}
        lede="Down / worse outcomes are shown in red; up / better outcomes are shown in green (each driver flexed by its own ± range, shown in the table). Numbers at the bar tips show the resulting KPI at each extreme."
      >
        <div
          className="card elevated card-pad-lg"
          style={{ position: "relative" }}
        >
          <div style={{ position: "absolute", top: 12, right: 12, zIndex: 2 }}>
            <ChartDownloadButton
              selector='svg[data-chart="tornado"]'
              filename="tornado.png"
            />
          </div>
          <div ref={tornadoRef}>
            <Tornado
              rows={rows}
              base={base}
              width={Math.max(320, Math.floor(tornadoWidth))}
              rowHeight={36}
              valueFormat={opt.fmt}
            />
          </div>
        </div>
      </Section>

      <Section eyebrow="Detail" title="Driver-by-driver table">
        <div
          className="card elevated table-scroll"
          tabIndex={0}
          role="region"
          aria-label="Driver-by-driver sensitivity table"
        >
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Driver</th>
                <th scope="col">Flex range</th>
                <th scope="col">Worse ↓</th>
                <th scope="col">Baseline</th>
                <th scope="col">Better ↑</th>
                <th scope="col">Total spread</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const driver = Model.DRIVERS.find((d) => d.key === r.variable);
                const bars = Model.tornadoBars(r);
                return (
                  <tr key={r.variable}>
                    <td className="row-label">{r.label}</td>
                    <td className="muted">
                      ±{Math.round((driver?.range || 0) * 100)}%
                    </td>
                    <td style={{ color: "hsl(var(--c-destructive))" }}>
                      ↓ {opt.fmt(bars.worse)}
                    </td>
                    <td>{opt.fmt(r.base)}</td>
                    <td style={{ color: "hsl(var(--c-success))" }}>
                      ↑ {opt.fmt(bars.better)}
                    </td>
                    <td
                      className="row-emph"
                      style={{ background: "transparent" }}
                    >
                      {opt.fmt(bars.spread)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}
